// VERIFY: cały plik, brak w mockupie. Sekcja „Command Center" jest ukryta w menu, a mockup jej
// nie zna, więc całość to tłumaczenie własne. Słownik terminów jak w reszcie aplikacji:
// visitor/caller = mieszkaniec, agent (człowiek) = urzędnik, AI agent = agent AI,
// claim = przejmij, handoff = przekazanie. Kolejność kluczy idzie po komponentach.
export default {
  // Etykiety pól powtarzają się w panelu bocznym, tabeli historii i szufladzie transkrypcji.
  fields: {
    id: 'ID',
    name: 'Imię i nazwisko',
    phone: 'Numer telefonu',
    visitor: 'Mieszkaniec',
    channel: 'Kanał',
    status: 'Status',
    agent: 'Urzędnik',
    language: 'Język',
    created: 'Utworzono',
    unassigned: 'Nieprzypisany',
  },

  visitor: {
    // Fallback etykiety rozmówcy, gdy backend nie zna ani imienia, ani numeru.
    anonymous: 'Anonimowy mieszkaniec',
    disconnected: 'Rozłączono',
  },

  // Wartości `webchat_status` z API (enum zostaje angielski w logice, tłumaczymy etykietę).
  status: {
    bot: 'Bot',
    queued: 'W kolejce',
    live: 'Na żywo',
    closed: 'Zamknięta',
    claiming: 'Przejmowanie',
  },

  // Stan przeglądarkowego urządzenia Twilio (konferencja i most tłumaczenia).
  deviceStatus: {
    idle: 'Bezczynne',
    initializing: 'Inicjowanie…',
    ready: 'Gotowe',
    connecting: 'Łączenie…',
    connected: 'Połączono',
    error: 'Błąd',
  },

  mic: {
    mute: 'Wycisz mikrofon',
    unmute: 'Włącz mikrofon',
  },

  // Etykiety kroków potoku tłumaczenia; ten sam zestaw czyta pasek mostu i panel czatu.
  bridge: {
    activity: {
      visitor: {
        transcribing: 'Mieszkaniec mówi…',
        translating: 'Tłumaczenie wypowiedzi mieszkańca…',
        speaking: 'Odtwarzanie tłumaczenia dla urzędnika…',
      },
      agent: {
        transcribing: 'Urzędnik mówi…',
        translating: 'Tłumaczenie wypowiedzi urzędnika…',
        speaking: 'Odtwarzanie dla mieszkańca…',
      },
    },
    statusBar: {
      aiHandling: 'Rozmowę obsługuje agent AI',
      callerLanguage: 'Język mieszkańca: {{language}}',
      connecting: 'Łączenie mostu tłumaczenia…',
      translating: 'Tłumaczenie: {{caller}} ↔ {{agent}}',
      // VERIFY: język urzędnika, gdy nie znamy kodu; tekst doklejany do nazwy języka z Intl.
      defaultAgentLanguage: 'angielski',
    },
  },

  header: {
    // VERIFY: „Command Center" to nazwa produktu, „Centrum obsługi" to nasze tłumaczenie.
    title: 'Centrum obsługi',
    // Etykiety zakładek „Na żywo" / „Historia" tłumaczy `CustomSwitch` (`common:switch.*`).
    availability: {
      available: 'Dostępny',
      unavailable: 'Niedostępny',
      availableTooltip: 'Przyjmowanie nowych rozmów',
      unavailableTooltip: 'Przyjmowanie rozmów wstrzymane',
    },
    translate: 'Tłumaczenie',
    // Języki urzędnika do wyboru w przełączniku tłumaczenia (wartości `pl-PL` itd. to kontrakt z API).
    languages: {
      polish: 'Polski',
      english: 'Angielski',
      spanish: 'Hiszpański',
    },
    sound: {
      on: 'Dźwięki alertów: włączone',
      off: 'Dźwięki alertów: wyłączone',
      mute: 'Wycisz alerty',
      unmute: 'Włącz alerty',
    },
    stats: {
      queued: 'W kolejce',
      queuedTooltip: 'Mieszkańcy oczekujący w kolejce',
      longestWait: 'Najdłuższe oczekiwanie',
      longestWaitTooltip: 'Najdłuższy bieżący czas oczekiwania',
      live: 'Na żywo',
      bot: 'Bot',
      voice: 'Głosowe',
      sms: 'SMS',
      agents: 'Urzędnicy',
    },
    // Jednostki czasu oczekiwania w kafelku „Najdłuższe oczekiwanie".
    wait: {
      seconds: '{{value}} s',
      minutes: '{{value}} min',
    },
  },

  sessionList: {
    title: 'Sesje',
    searchPlaceholder: 'Szukaj mieszkańców…',
    // Grupy „W kolejce" i „Na żywo" biorą etykiety z `status.*`.
    groups: {
      // VERIFY: „AI handling" (AI obsługuje rozmowę), obejmuje czat z botem i rozmowy głosowe AI.
      bot: 'Obsługa AI',
    },
    empty: {
      noMatches: 'Brak wyników',
      noMatchesHint: 'Spróbuj innego imienia, numeru telefonu lub SID.',
      noSessions: 'Brak aktywnych sesji',
      noSessionsHint: 'Sesje pojawią się tutaj, gdy mieszkańcy rozpoczną rozmowę, czat lub SMS.',
    },
  },

  sessionCard: {
    returning: 'Powracający',
    typing: 'pisze…',
    idle: 'Bezczynność {{count}} min',
  },

  onHold: {
    // VERIFY: „On hold" (rozmówca czeka na linii przy muzyce), brak utrwalonego polskiego terminu.
    label: 'Oczekuje',
    ariaLabel: 'Mieszkaniec oczekuje na linii',
    tooltipSince: 'Mieszkaniec oczekuje na linii od {{time}} ({{ago}})',
    tooltipNoSince:
      'Mieszkaniec oczekuje na linii i słyszy muzykę. Przejmij rozmowę, aby się połączyć.',
  },

  chatPanel: {
    empty: {
      title: 'Nie wybrano rozmowy',
      hint: 'Wybierz sesję z listy, aby zobaczyć transkrypcję, wysłać odpowiedź lub przejąć rozmowę.',
    },
    aiCallTag: 'Rozmowa AI',
    waitingForTranscript: 'Oczekiwanie na transkrypcję…',
    visitorTyping: 'Mieszkaniec pisze…',
    newMessages: 'Nowe wiadomości',
    emailThread: 'Wątek e-mail – pełna treść wiadomości dostępna w eksporcie rozmów.',
    voiceCall: 'Rozmowa głosowa – transkrypcja będzie dostępna po zakończeniu rozmowy.',
    listenOnlyAi: 'Tylko odsłuch – rozmowę obsługuje agent AI.',
    listenOnlyLive: 'Tylko odsłuch – trwa rozmowa głosowa na żywo.',
    bridgeActive: 'Most tłumaczenia aktywny – {{language}}',
    takeOver: 'Przejmij',
    placeholder: {
      smsAssigned: 'Wpisz odpowiedź SMS…',
      smsUnassigned: 'Odpowiedź SMS – wysłanie przejmie rozmowę od AI.',
      live: 'Wpisz wiadomość…',
      notLive: 'Przejmij rozmowę, aby odpowiedzieć',
    },
    readOnly: {
      email: 'Wątek e-mail – odpowiedzi nie są obsługiwane w Centrum obsługi.',
      voice: 'Tylko podgląd – rozmowy głosowe są tu obserwowane, a nie obsługiwane.',
    },
  },

  liveTranscript: {
    sender: {
      caller: 'Mieszkaniec',
      agent: 'Urzędnik',
      aiAgent: 'Agent AI',
    },
    correction: '(korekta)',
    audio: {
      playOriginal: 'Odtwórz oryginał',
      stopOriginal: 'Zatrzymaj oryginał',
      playTranslation: 'Odtwórz tłumaczenie',
      stopTranslation: 'Zatrzymaj tłumaczenie',
    },
    // Separator końca części prowadzonej przez człowieka; powód idzie z `reasons`.
    bridgeEnd: {
      divider: '– {{reason}} ({{time}}) –',
      reasons: {
        closedByAgent: 'Urzędnik zamknął rozmowę',
        closedByVisitor: 'Mieszkaniec zamknął rozmowę',
        bridgeEnded: 'Most zakończony',
        callerHungUpDuringClaim: 'Mieszkaniec rozłączył się podczas przejmowania',
      },
    },
  },

  actionsSidebar: {
    emptyState: 'Wybierz rozmowę, aby zobaczyć dostępne akcje.',
    tabs: {
      info: 'Informacje',
      profile: 'Profil',
      notes: 'Notatki',
    },
    infoTitle: 'Dane mieszkańca',
    claim: 'Przejmij rozmowę',
    claimAiCall: 'Przejmij rozmowę AI',
    connecting: 'Łączenie z mieszkańcem…',
    reassign: 'Przekaż innemu urzędnikowi',
    close: 'Zamknij rozmowę',
    closeConfirm: {
      title: 'Zamknij rozmowę',
      content: 'Czy na pewno zamknąć tę rozmowę?',
      contentBridge: 'Spowoduje to zakończenie mostu tłumaczenia i zamknięcie rozmowy. Kontynuować?',
    },
    // Ostrzeżenie z mostu: skonfigurowany głos odrzucony przez ElevenLabs. `role` to dopełniacz z `voiceRejectedRole`.
    voiceRejected:
      'Skonfigurowany głos {{role}} został odrzucony przez ElevenLabs. Most przełączył się na domyślny głos dla języka <strong>{{language}}</strong>. Otwórz kartę Języki agenta AI i wybierz głos ponownie.',
    // VERIFY: fallback, gdy most nie podał języka; wstawiany po słowach „dla języka".
    voiceRejectedUnknownLanguage: 'rozmowy',
    voiceRejectedRole: {
      visitor: 'mieszkańca',
      agent: 'urzędnika',
    },
    bridgeTag: {
      translating: 'Most: {{status}} · tłumaczenie',
      // VERIFY: „passthrough" (dźwięk przechodzi bez tłumaczenia).
      passthrough: 'Most: {{status}} · bez tłumaczenia',
    },
    translation: {
      disable: 'Wyłącz tłumaczenie',
      enable: 'Włącz tłumaczenie',
    },
    reconnect: 'Połącz ponownie',
    notes: {
      label: 'Notatki wewnętrzne',
      placeholder: 'Dodaj notatki wewnętrzne…',
    },
    transfer: {
      ok: 'Przekaż',
      selectPlaceholder: 'Wybierz urzędnika',
      notesPlaceholder: 'Notatki do przekazania (opcjonalne)',
      agentOption_one: '{{name}} ({{count}} aktywna rozmowa)',
      agentOption_few: '{{name}} ({{count}} aktywne rozmowy)',
      agentOption_many: '{{name}} ({{count}} aktywnych rozmów)',
      agentOption_other: '{{name}} ({{count}} aktywnej rozmowy)',
      agentOptionUnavailable_one: '{{name}} ({{count}} aktywna rozmowa, niedostępny)',
      agentOptionUnavailable_few: '{{name}} ({{count}} aktywne rozmowy, niedostępny)',
      agentOptionUnavailable_many: '{{name}} ({{count}} aktywnych rozmów, niedostępny)',
      agentOptionUnavailable_other: '{{name}} ({{count}} aktywnej rozmowy, niedostępny)',
    },
  },

  conference: {
    // VERIFY: „From Number" (numer, z którego system dzwoni do uczestników).
    fromNumber: 'Numer wychodzący',
    fromNumberPlaceholder: 'Wybierz numer telefonu',
    visitorPhone: 'Numer telefonu mieszkańca',
    translatorPhone: 'Numer telefonu tłumacza',
    // VERIFY: przykładowy numer w polskim formacie zamiast +1234567890.
    phonePlaceholder: '+48123456789',
    phonePlaceholderOptional: '+48123456789 (opcjonalnie)',
    liveTranslation: 'Tłumaczenie na żywo',
    visitorLanguage: 'Język mieszkańca: {{language}}',
    start: 'Rozpocznij konferencję',
    startTranslated: 'Rozpocznij konferencję z tłumaczeniem',
    end: 'Zakończ konferencję',
    // VERIFY: „Participants dialed".
    participantsDialed: 'Zadzwoniono do uczestników: {{value}}',
    confirm: {
      title: 'Rozpoczęcie konferencji',
      titleTranslated: 'Rozpoczęcie konferencji z tłumaczeniem',
      ok: 'Rozpocznij',
      bodyTranslated:
        'Przeglądarka zostanie połączona z konferencją z tłumaczeniem AI na żywo. Mieszkaniec usłyszy tłumaczenie w swoim języku, a urzędnik usłyszy angielski.',
      bodyOne: 'Przeglądarka zostanie połączona z konferencją, a system wybierze numer uczestnika.',
      bodyMany: 'Przeglądarka zostanie połączona z konferencją, a system wybierze numery uczestników.',
    },
    // Te same komunikaty czyta komponent (toast) i hook (fallback, gdy błąd nie jest `Error`).
    errors: {
      start: 'Nie udało się rozpocząć konferencji',
      startTranslated: 'Nie udało się rozpocząć konferencji z tłumaczeniem',
      end: 'Nie udało się zakończyć konferencji',
    },
  },

  historyPanel: {
    filters: {
      allStatuses: 'Wszystkie statusy',
      allChannels: 'Wszystkie kanały',
    },
    drawer: {
      title: 'Transkrypcja rozmowy',
      jumpToLive: 'Przejdź do rozmowy na żywo',
      showingOriginal:
        'Widoczny język oryginalny. Kliknij, aby pokazać tłumaczenie na angielski.',
      showingEnglish:
        'Widoczne tłumaczenie na angielski. Kliknij, aby pokazać język oryginalny.',
      toggleTranslation: 'Przełącz tłumaczenie transkrypcji',
      original: 'Oryginał',
      english: 'Angielski',
    },
    recordings: {
      ai: 'Nagranie AI',
      voice: 'Nagranie rozmowy',
      // VERIFY: „Live agent recording" (nagranie rozmowy po przejęciu przez człowieka).
      liveAgent: 'Nagranie rozmowy z urzędnikiem',
    },
    empty: {
      noTranscript: 'Brak transkrypcji.',
      noPerTurnTranscript:
        'Transkrypcja wypowiedzi nie jest jeszcze dostępna dla tego typu rozmowy.',
      noMessages: 'Brak wiadomości w tej rozmowie.',
    },
  },

  recordingRow: {
    refresh: 'Odśwież',
    downloadAria: 'Pobierz: {{label}}',
    processing:
      'Nagranie jest jeszcze przetwarzane – Twilio zwykle dostarcza je w ciągu 1–2 minut po zakończeniu rozmowy.',
    unavailable: 'Niedostępne dla tej rozmowy.',
  },

  screenShare: {
    title: 'Ekran mieszkańca',
    titleEnded: 'Ekran mieszkańca (zakończono)',
  },

  logsSidebar: {
    show: 'Pokaż logi',
    hide: 'Ukryj logi',
    toggleLabel: 'Logi',
    header: 'Logi mostu tłumaczenia',
    ended: 'zakończona',
    // Zdanie z wymuszonym łamaniem wiersza, dlatego `<Trans>` z tagiem `<br/>`.
    empty: 'Wybierz rozmowę,<br/>aby zobaczyć logi.',
  },

  translationLog: {
    emptyTitle: 'Brak zdarzeń tłumaczenia.',
    emptyHint: 'Uruchom most tłumaczenia, aby zobaczyć logi na żywo.',
    steps: {
      // VERIFY: skróty STT (rozpoznawanie mowy) i TTS (synteza mowy) zostają jak w oryginale.
      transcribing: 'Mówienie (STT)',
      translating: 'Tłumaczenie',
      speaking: 'Odtwarzanie (TTS)',
    },
    roles: {
      visitor: 'Mieszkaniec',
      agent: 'Urzędnik',
    },
    duration: {
      milliseconds: '{{value}} ms',
      seconds: '{{value}} s',
    },
  },

  userProfile: {
    loadError: 'Nie udało się wczytać profilu tego mieszkańca.',
    firstContact: 'Pierwszy kontakt',
    priorContacts_one: '{{count}} wcześniejszy kontakt',
    priorContacts_few: '{{count}} wcześniejsze kontakty',
    priorContacts_many: '{{count}} wcześniejszych kontaktów',
    priorContacts_other: '{{count}} wcześniejszego kontaktu',
    email: 'Adres e-mail',
    lastContact: 'Ostatni kontakt',
    accountInfo: 'Dane konta',
    noProfileDocument: 'Brak dokumentu profilu.',
    recentConversations: 'Ostatnie rozmowy',
    noPriorConversations: 'Brak wcześniejszych rozmów.',
  },

  // Toasty z hooków `useWebchatActions`. Komunikat z backendu ma pierwszeństwo, te są fallbackiem.
  actions: {
    claimed: 'Rozmowa przejęta.',
    claimFailed: 'Nie udało się przejąć rozmowy.',
    closed: 'Rozmowa zamknięta.',
    closeFailed: 'Nie udało się zamknąć rozmowy.',
    transferred: 'Rozmowa przekazana.',
    transferFailed: 'Nie udało się przekazać rozmowy.',
    sendFailed: 'Nie udało się wysłać wiadomości.',
    notesSaved: 'Notatki zapisane.',
    notesFailed: 'Nie udało się zapisać notatek.',
  },

  // Powiadomienia o zdarzeniach z kanału WebSocket (`useWebchatAgent`).
  notifications: {
    voiceCall: {
      title: 'Nowa rozmowa głosowa',
      description: 'Rozpoczęła się rozmowa głosowa na żywo.',
    },
    sms: {
      title: 'Nowy SMS',
      description: 'Nadeszła nowa rozmowa SMS.',
    },
    handoff: {
      title: 'Przekazanie do urzędnika',
      description: 'Rozmowa została przekazana urzędnikowi.',
    },
    aiCall: {
      title: 'Nowa rozmowa AI',
      description: 'Mieszkaniec połączył się z agentem AI.',
    },
    session: {
      title: 'Nowa sesja',
      description: 'Rozpoczęła się nowa sesja.',
    },
    queuedVoiceCall: {
      title: 'Nowa rozmowa głosowa w kolejce',
      description: 'Mieszkaniec oczekuje na linii na połączenie z urzędnikiem.',
    },
    queuedAiCall: {
      title: 'Nowa rozmowa AI w kolejce',
      description: 'Rozmowa głosowa AI oczekuje na przejęcie.',
    },
    queuedChat: {
      title: 'Nowy czat w kolejce',
      description: 'Mieszkaniec oczekuje w kolejce.',
    },
    transferFailed: {
      title: 'Przekazanie nie powiodło się',
      description: 'Nie udało się przekazać rozmowy. Spróbuj przejąć ją ponownie.',
    },
    newMessage: 'Nowa wiadomość',
  },

  // Fallback komunikatu, gdy Twilio nie zwróciło własnego (`err.message` z Twilio ma pierwszeństwo).
  voiceDevice: {
    connectFailed: 'Nie udało się połączyć',
  },
} as const;
